import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { StarRating } from '../components/StarRating';
import { ListSkeleton, SkeletonBar, Spinner, Button } from '../components/ui';

const STATUS_COLORS: Record<string, string> = {
  queued: T.textSecondary,
  pending: T.textSecondary,
  running: T.primary,
  leased: T.primary,
  done: T.success,
  failed: T.danger,
  cancelled: T.textSecondary,
};

const STATUS_LABELS: Record<string, string> = {
  queued: 'Na fila',
  pending: 'Aguardando',
  running: 'Processando',
  leased: 'Processando',
  done: 'Concluído',
  failed: 'Erro',
  cancelled: 'Cancelado',
};

const STATUS_ICONS: Record<string, string> = {
 queued: '',
 pending: '',
 running: '',
 leased: '',
 done: '',
 failed: '',
 cancelled: '',
};

const JOB_TYPE_LABELS: Record<string, string> = {
  llm_prompt: 'Inteligência Artificial',
  ai_prompt: 'Inteligência Artificial',
  http_fetch: 'Requisição HTTP',
  scrape_html: 'Extração de Página',
  seo_fetch_serp: 'Pesquisa Google (SERP)',
  generate_artifact_markdown: 'Gerar Documento',
  noop: 'Passo Vazio',
};

const JOB_TYPE_ICONS: Record<string, string> = {
 llm_prompt: '',
 ai_prompt: '',
 http_fetch: '',
 scrape_html: '',
 seo_fetch_serp: '',
 generate_artifact_markdown: '',
 noop: '',
};

const ARTIFACT_ICONS: Record<string, string> = {
 report_md: '',
 report_html: '',
 report_json: '',
 file: '',
};

function elapsed(start: string, end?: string) {
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const diff = Math.max(0, e - s);
  if (diff < 1000) return `${diff}ms`;
  if (diff < 60000) return `${Math.round(diff / 1000)}s`;
  return `${Math.floor(diff / 60000)}m ${Math.round((diff % 60000) / 1000)}s`;
}

function formatDate(d: string) {
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function RunViewPage({ runId }: { runId?: string; path?: string }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [elapsedStr, setElapsedStr] = useState('');
  const pollRef = useRef<any>(null);
  const timerRef = useRef<any>(null);

  const fetchRun = async () => {
    if (!runId) return;
    try {
      const res = await api.get<any>(`/runs/${runId}`);
      setData(res);
      setLoading(false);
      const status = res?.run?.status;
      if (status === 'done' || status === 'failed' || status === 'cancelled') {
        if (pollRef.current) clearInterval(pollRef.current);
      }
    } catch { setLoading(false); }
  };

  useEffect(() => {
    fetchRun();
    pollRef.current = setInterval(fetchRun, 3000);
    timerRef.current = setInterval(() => {
      setData((d: any) => {
        if (!d?.run?.started_at) return d;
        const end = d.run.finished_at || undefined;
        setElapsedStr(elapsed(d.run.started_at, end));
        return d;
      });
    }, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [runId]);

  const cancelRun = async () => {
    if (!data?.run) return;
    setCancelling(true);
    try { await api.post(`/runs/${runId}/cancel`); fetchRun(); } catch {}
    setCancelling(false);
  };

  if (loading) return <div style={{ padding: 60, maxWidth: 600, margin: '0 auto' }}><ListSkeleton rows={3} /></div>;
  if (!data?.run) return <div style={{ padding: 60, textAlign: 'center', color: T.textSecondary, fontSize: 16 }}>Execução não encontrada</div>;

  const { run, jobs, artifacts, summary } = data;

  // Map artifacts by source_job_id for per-step download
  const artifactsByJob: Record<string, any[]> = {};
  if (artifacts) {
    for (const art of artifacts) {
      const jid = art.source_job_id;
      if (jid) {
        if (!artifactsByJob[jid]) artifactsByJob[jid] = [];
        artifactsByJob[jid].push(art);
      }
    }
  }

  const isActive = run.status === 'queued' || run.status === 'running';
  const isTerminal = run.status === 'done' || run.status === 'failed' || run.status === 'cancelled';
  const progress = summary.total > 0 ? Math.round(((summary.done + summary.failed) / summary.total) * 100) : 0;

  // Extract prompt info from first job payload
  let promptInfo: any = null;
  if (jobs.length > 0) {
    try {
      const payload = typeof jobs[0].payload === 'string' ? JSON.parse(jobs[0].payload) : jobs[0].payload;
      promptInfo = payload;
    } catch {}
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 16px 40px' }}>

      {/* ══ Header Card ══ */}
      <div style={{
        background: T.bgCard, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: '20px 24px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ flex: 1 }}>
            {run.agent_name && (
              <div style={{ fontSize: 11, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>
 Agente: {run.agent_name}
              </div>
            )}
            <div style={{ fontSize: 28, marginBottom: 4 }}>
 {run.action_icon || ''} {run.action_name || 'Execução'}
            </div>
            <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
              ID: <code style={{ background: T.bg, padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{run.id}</code>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{
              padding: '8px 18px', borderRadius: 24, fontSize: 14, fontWeight: 700,
              background: STATUS_COLORS[run.status] + '18',
              color: STATUS_COLORS[run.status],
              border: `1.5px solid ${STATUS_COLORS[run.status]}44`,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {STATUS_ICONS[run.status]} {STATUS_LABELS[run.status] || run.status}
            </span>
            {isActive && (
              <Button variant="ghost" class="btn" style={{ background: T.danger, color: T.text, fontSize: 13, padding: '8px 18px', borderRadius: 8 }} onClick={cancelRun} disabled={cancelling}>
                {cancelling ? '...' : 'Cancelar'}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
          {run.runner_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 20 }}></span>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Executor</div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{run.runner_name}</div>
              </div>
            </div>
          )}
          {elapsedStr && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 20 }}>{isActive ? '' : ''}</span>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tempo</div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{elapsedStr}</div>
              </div>
            </div>
          )}
          {run.started_at && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 20 }}></span>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Início</div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{formatDate(run.started_at)}</div>
              </div>
            </div>
          )}
          {run.finished_at && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 20 }}></span>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Fim</div>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{formatDate(run.finished_at)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══ Rating ══ */}
      {isTerminal && (
        <div style={{
          background: T.bgCard, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: '16px 24px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6 }}>Como foi esta execução?</div>
            <StarRating taskId={run.id} size={28} />
          </div>
        </div>
      )}

      {/* ══ Agente Responsável ══ */}
      {(run.agent_name || run.action_name) && (
        <details style={{ marginBottom: 20 }}>
          <summary style={{
            fontSize: 14, color: T.text, cursor: 'pointer', padding: '14px 20px',
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12,
            listStyle: 'none',
          }}>
            <span style={{
              width: 36, height: 36, borderRadius: 10, background: '#6366f118',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
 }}></span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8,
                  padding: '2px 8px', borderRadius: 6, background: '#6366f122', color: T.purple,
                }}>Agente</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                  {run.agent_name || 'Agente'}
                </span>
              </div>
              {run.agent_description && (
                <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4, lineHeight: 1.4 }}>
                  {run.agent_description}
                </div>
              )}
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>
 {run.category_name ? `${run.category_name} · ` : ''}{run.execution_mode === 'llm' ? 'LLM' : 'Runner'}
              </div>
            </div>
            <span style={{ fontSize: 12, color: T.textMuted, transition: 'transform 0.2s' }}>▼</span>
          </summary>
          <div style={{
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '16px 20px',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
              {run.agent_name && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Agente</div>
                  <div style={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{run.agent_name}</div>
                </div>
              )}
              {run.category_name && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Categoria</div>
                  <div style={{ fontSize: 14, color: T.text }}>{run.category_name}</div>
                </div>
              )}
              {run.action_name && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Action</div>
 <div style={{ fontSize: 14, color: T.text }}>{run.action_icon || ''} {run.action_name}</div>
                </div>
              )}
              {run.execution_mode && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Modo</div>
 <div style={{ fontSize: 14, color: T.text }}>{run.execution_mode === 'llm' ? 'LLM' : 'Runner'}</div>
                </div>
              )}
            </div>
            {run.action_description && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Descrição</div>
                <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6 }}>{run.action_description}</div>
              </div>
            )}
            {promptInfo?.system_prompt && (
              <div style={{ marginBottom: 16 }}>
 <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Prompt do Sistema</div>
                <div style={{
                  background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
                  padding: 16, fontSize: 13, color: T.text, lineHeight: 1.7,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflowY: 'auto',
                }}>
                  {promptInfo.system_prompt}
                </div>
              </div>
            )}
            {promptInfo?.user_message && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Dados Enviados pelo Usuário</div>
                <div style={{
                  background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8,
                  padding: 16, fontSize: 13, color: T.text, lineHeight: 1.7,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {promptInfo.user_message}
                </div>
              </div>
            )}
            {promptInfo?.output_formats && promptInfo.output_formats.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Formatos de Entrega</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {promptInfo.output_formats.map((f: string) => (
                    <span key={f} style={{
                      padding: '4px 12px', borderRadius: 6, fontSize: 12,
                      background: T.primaryBgSubtle, border: `1px solid ${T.primaryBg}`,
                      color: T.primary,
                    }}>{f.toUpperCase()}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </details>
      )}

      {/* ══ Progress Bar ══ */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ height: 8, background: T.bg, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4, transition: 'width 0.5s ease',
            width: isActive && progress === 0 ? '100%' : `${progress}%`,
            background: isActive && progress === 0
              ? `linear-gradient(90deg, transparent, ${T.primary}, transparent)`
              : run.status === 'failed' ? T.danger : 'linear-gradient(90deg, #22c55e, #16a34a)',
            animation: isActive && progress === 0 ? 'shimmer 2s infinite' : 'none',
          }} />
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 13, color: T.textSecondary, marginTop: 10 }}>
 <span style={{ color: T.success, display: 'flex', alignItems: 'center', gap: 4 }}> {summary.done} concluída{summary.done !== 1 ? 's' : ''}</span>
 {summary.failed > 0 && <span style={{ color: T.danger, display: 'flex', alignItems: 'center', gap: 4 }}> {summary.failed} erro{summary.failed !== 1 ? 's' : ''}</span>}
 {summary.running > 0 && <span style={{ color: T.primary, display: 'flex', alignItems: 'center', gap: 4 }}> {summary.running} processando</span>}
 {summary.pending > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}> {summary.pending} aguardando</span>}
        </div>
      </div>

      {/* ══ Integration Error Alert ══ */}
      {jobs.some((j: any) => j.error && (
        j.error.includes('authentication_error') || j.error.includes('invalid') || j.error.includes('API key') || j.error.includes('não encontrada') || j.error.includes('401') || j.error.includes('403')
      )) && (
        <div style={{ padding: '16px 20px', background: `${T.warningBgSubtle}`, border: `1px solid ${T.warningBgStrong}`, borderRadius: 10, marginBottom: 20 }}>
 <strong style={{ color: T.warning, fontSize: 15 }}>Erro de integração detectado</strong>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: '8px 0 12px' }}>
            Uma ou mais chaves de API estão inválidas ou ausentes. Verifique suas integrações.
          </p>
          <a href="/integrations" class="btn btn-primary" style={{ fontSize: 13, padding: '8px 20px', textDecoration: 'none' }}>Configurar Integrações</a>
        </div>
      )}

      {/* Prompt / Instructions moved inside Agente section below */}

      {/* ══ Jobs / Etapas ══ */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
 Etapas da Execução <span style={{ fontSize: 13, color: T.textMuted, fontWeight: 400 }}>({jobs.length})</span>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: T.textSecondary, fontWeight: 400 }}>
            {summary.done} de {summary.total} concluída{summary.total !== 1 ? 's' : ''}
          </span>
        </h2>
        {jobs.map((job: any, idx: number) => {
          const isExpanded = expandedJob === job.id;
          const jobActive = job.status === 'running' || job.status === 'leased';
          const jobDuration = job.duration_ms ? `${(job.duration_ms / 1000).toFixed(1)}s` :
            job.started_at && !jobActive ? elapsed(job.started_at, job.finished_at) : '';
          let output: any = null;
          try { output = job.result_output ? JSON.parse(job.result_output) : null; } catch { output = job.result_output; }
          const outputText = typeof output === 'string' ? output : output?.toString?.() || '';
          const typeLabel = JOB_TYPE_LABELS[job.job_type] || job.job_type;
 const typeIcon = JOB_TYPE_ICONS[job.job_type] || '';

          // Extract model and step name from payload
          let jobModel = '';
          let stepName = '';
          try {
            const p = typeof job.payload === 'string' ? JSON.parse(job.payload) : job.payload;
            const m = p?.__model || p?.model || '';
            if (m) jobModel = m === 'opus' ? 'Claude Opus 4.6' : m === 'sonnet' ? 'Claude Sonnet 4.5' : m === 'haiku' ? 'Claude Haiku 4.5' : m;
            stepName = p?.__step_name || p?.name || '';
          } catch {}
          // Use step_name from API (extracted from action's job_graph)
          if (!stepName && job.step_name) stepName = job.step_name;
          // Fallback: use job_key if meaningful
          if (!stepName && job.job_key && !job.job_key.match(/^step-\d+$/)) {
            stepName = job.job_key;
          }

          return (
            <div key={job.id} style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: 10, marginBottom: 10, overflow: 'hidden',
              borderLeft: `4px solid ${STATUS_COLORS[job.status]}`,
            }}>
              {/* Job header - clickable */}
              <div
                style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
                onClick={() => setExpandedJob(isExpanded ? null : job.id)}
              >
                {/* Step number + icon */}
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: STATUS_COLORS[job.status] + '18',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0,
                }}>
                  {jobActive ? (
                    <Spinner size="sm" inline />
                  ) : (
                    <span>{STATUS_ICONS[job.status]}</span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>
                    Etapa {idx + 1}{stepName ? `: ${stepName}` : `: ${typeLabel}`}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {jobModel && (
                      <span style={{
                        padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: jobModel.includes('Opus') ? '#7c3aed22' : jobModel.includes('Sonnet') ? 'T.warning22' : 'T.success22',
                        color: jobModel.includes('Opus') ? T.purple : jobModel.includes('Sonnet') ? T.warning : T.success,
                      }}>
                        {jobModel}
                      </span>
                    )}
 {jobDuration && <span> {jobDuration}</span>}
                  </div>
                </div>

                <span style={{
                  fontSize: 13, padding: '6px 14px', borderRadius: 20, fontWeight: 600,
                  background: STATUS_COLORS[job.status] + '18',
                  color: STATUS_COLORS[job.status],
                }}>
                  {STATUS_LABELS[job.status] || job.status}
                </span>

                {/* Download icon — only when job has artifacts */}
                {artifactsByJob[job.id] && artifactsByJob[job.id].length > 0 && (
                  <a
                    href={`/api/v1/runs/${runId}/artifacts/${artifactsByJob[job.id][0].id}?download=1`}
                    target="_blank"
                    rel="noopener"
                    title={`Baixar ${artifactsByJob[job.id].length} arquivo${artifactsByJob[job.id].length > 1 ? 's' : ''}`}
                    onClick={(e: Event) => e.stopPropagation()}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 32, height: 32, borderRadius: 8,
                      background: T.primaryBgSubtle,
                      color: T.primary,
                      textDecoration: 'none', fontSize: 16,
                      transition: 'background 0.2s',
                      flexShrink: 0,
                    }}
                  >
 
                  </a>
                )}

                <span style={{ fontSize: 14, color: T.textMuted, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
              </div>

              {/* Job error inline */}
              {job.error && !isExpanded && (
                <div style={{ padding: '0 20px 14px', fontSize: 13, color: T.danger, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
 <span></span>
                  <span>{job.error.length > 150 ? job.error.slice(0, 150) + '...' : job.error}</span>
                </div>
              )}

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${T.border}` }}>

                  {/* Timeline header - clean */}

                  {/* Timeline */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                    {[
 { icon: '', label: 'Criado', value: job.created_at },
 { icon: '', label: 'Iniciado', value: job.started_at },
 { icon: '', label: 'Finalizado', value: job.finished_at },
                    ].filter(t => t.value).map(t => (
                      <div key={t.label} style={{ background: T.bg, borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>{t.icon} {t.label}</div>
                        <div style={{ fontSize: 13, color: T.text }}>{new Date(t.value).toLocaleTimeString('pt-BR')}</div>
                      </div>
                    ))}
                    {jobDuration && (
                      <div style={{ background: T.bg, borderRadius: 8, padding: '10px 14px' }}>
 <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>Duração</div>
                        <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{jobDuration}</div>
                      </div>
                    )}
                  </div>

                  {/* Error */}
                  {job.error && (
                    <div style={{ marginTop: 16, padding: 16, background: `${T.dangerBgSubtle}`, border: `1px solid ${T.dangerBgStrong}`, borderRadius: 8, fontSize: 13, color: T.danger, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
 <strong style={{ display: 'block', marginBottom: 6 }}>Erro:</strong>
                      {job.error}
                    </div>
                  )}

                  {/* Output/Result */}
                  {outputText && (
                    <div style={{ marginTop: 16 }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>Resultado da IA:</div>
                      <div style={{
                        background: T.bg, border: `1px solid ${T.border}`,
                        borderRadius: 8, padding: 16, fontSize: 13, color: T.text,
                        maxHeight: 500, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        lineHeight: 1.7,
                      }}>
                        {outputText.length > 5000 ? outputText.slice(0, 5000) + '\n\n[... truncado]' : outputText}
                      </div>
                    </div>
                  )}

                  {/* Logs */}
                  {job.result_logs && (
                    <div style={{ marginTop: 16 }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>Logs de Execução:</div>
                      <pre style={{
                        background: T.bg, border: `1px solid ${T.bgSecondary}`, borderRadius: 8,
                        padding: 16, fontSize: 12, color: '#8b8', margin: 0,
                        maxHeight: 250, overflowY: 'auto', whiteSpace: 'pre-wrap',
                      }}>
                        {job.result_logs}
                      </pre>
                    </div>
                  )}

                  {/* Prompt da Etapa (collapsible) */}
                  {(() => {
                    try {
                      const pl = typeof job.payload === 'string' ? JSON.parse(job.payload) : (job.payload || {});
                      const sysPrompt = pl.system_prompt || pl.llm_config?.system_prompt || '';
                      const userMsg = pl.user_message || '';
                      if (!sysPrompt && !userMsg) return null;
                      return (
                        <details style={{ marginTop: 16 }}>
 <summary style={{ fontSize: 13, color: T.textMuted, cursor: 'pointer', padding: '8px 0' }}>Prompt da etapa</summary>
                          <div style={{ marginTop: 8 }}>
                            {sysPrompt && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>System Prompt</div>
                                <div style={{
                                  background: T.bg, border: `1px solid ${T.border}`, borderLeft: '3px solid #6366f1',
                                  borderRadius: 8, padding: 14, fontSize: 13, color: T.textSecondary,
                                  maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6,
                                }}>
                                  {sysPrompt.length > 2000 ? sysPrompt.slice(0, 2000) + '\n\n[... truncado]' : sysPrompt}
                                </div>
                              </div>
                            )}
                            {userMsg && (
                              <div>
                                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>User Message</div>
                                <div style={{
                                  background: T.bg, border: `1px solid ${T.border}`, borderLeft: '3px solid #22c55e',
                                  borderRadius: 8, padding: 14, fontSize: 13, color: T.textSecondary,
                                  maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6,
                                }}>
                                  {userMsg.length > 2000 ? userMsg.slice(0, 2000) + '\n\n[... truncado]' : userMsg}
                                </div>
                              </div>
                            )}
                          </div>
                        </details>
                      );
                    } catch { return null; }
                  })()}

                  {/* Payload + Technical */}
                  <details style={{ marginTop: 8 }}>
 <summary style={{ fontSize: 13, color: T.textMuted, cursor: 'pointer', padding: '8px 0' }}>Dados técnicos — {typeIcon} {job.job_type} · tentativa {job.attempts}/{job.max_attempts}</summary>
                    <pre style={{
                      background: T.bg, border: `1px solid ${T.bgSecondary}`, borderRadius: 8,
                      padding: 16, fontSize: 12, color: T.textSecondary, marginTop: 8,
                      maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap',
                    }}>
                      {JSON.stringify(job.payload ? JSON.parse(job.payload) : {}, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* ══ Artifacts / Entregas ══ */}
      {artifacts && artifacts.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
 Entregas <span style={{ fontSize: 13, color: T.textMuted, fontWeight: 400 }}>({artifacts.length} arquivo{artifacts.length !== 1 ? 's' : ''})</span>
          </h2>
          {(() => {
            const sorted = [...artifacts].sort((a: any, b: any) => {
              if (a.name?.includes('report.json') && !b.name?.includes('report.json')) return 1;
              if (!a.name?.includes('report.json') && b.name?.includes('report.json')) return -1;
              return (a.created_at || '').localeCompare(b.created_at || '');
            });
            const nonJson = sorted.filter((a: any) => !a.name?.includes('report.json'));
            const finalId = nonJson.length > 0 ? nonJson[nonJson.length - 1]?.id : null;
            const finalArt = sorted.find((a: any) => a.id === finalId);
            const others = sorted.filter((a: any) => a.id !== finalId);

            const renderArt = (art: any, isFinal: boolean) => {
 const icon = ARTIFACT_ICONS[art.type] || '';
              const ext = art.name?.split('.').pop()?.toUpperCase() || '';
              const viewUrl = `/api/v1/runs/${runId}/artifacts/${art.id}`;
              const downloadUrl = `${viewUrl}?download=1`;
              return (
                <div key={art.id} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: isFinal ? '20px 24px' : '14px 18px',
                  background: isFinal ? 'linear-gradient(135deg, #1a1a3e, #1e1e40)' : T.bgCard,
                  border: isFinal ? `1.5px solid ${T.primary}` : `1px solid ${T.border}`,
                  borderRadius: 10, marginBottom: 8,
                }}>
                  <div style={{
                    width: isFinal ? 48 : 36, height: isFinal ? 48 : 36, borderRadius: 10,
                    background: T.primaryBgSubtle, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: isFinal ? 24 : 18, flexShrink: 0,
                  }}>
                    {icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: isFinal ? 16 : 14, color: T.text }}>{art.name}</strong>
                      {isFinal && (
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: T.primary, color: T.text, fontWeight: 700 }}>
                          Resultado Final
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, display: 'flex', gap: 10 }}>
                      <span>{ext}</span>
                      {art.size_bytes && <span>{art.size_bytes > 1024 ? `${Math.round(art.size_bytes / 1024)}KB` : `${art.size_bytes}B`}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <a href={downloadUrl} target="_blank" rel="noopener"
                      style={{
                        padding: isFinal ? '10px 16px' : '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        background: T.primary, color: T.text, textDecoration: 'none',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                      Baixar
                    </a>
                  </div>
                </div>
              );
            };

            return (
              <>
                {finalArt && renderArt(finalArt, true)}
                {others.length > 0 && (
                  <details>
                    <summary style={{
                      fontSize: 12, color: T.textMuted, cursor: 'pointer', textAlign: 'center',
                      padding: '8px 0', listStyle: 'none', userSelect: 'none',
                    }}>
                      <span style={{ borderBottom: '1px dashed #444', paddingBottom: 1 }}>
                        Ver todos os arquivos ({others.length})
                      </span>
                    </summary>
                    <div style={{ marginTop: 8 }}>
                      {others.map((art: any) => renderArt(art, false))}
                    </div>
                  </details>
                )}
              </>
            );
          })()}
        </section>
      )}

      {/* Agente + Timeline moved above progress bar */}

      {/* Animations */}
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
      `}</style>
    </div>
  );
}
